import React, { useState } from "react";
import SoundFx from "./SoundFx";
import "./css/SoundFxControls.css";

const SoundFxControls = ({
  effects,
  onChange,
  compact = false,
  showOnlyEq = false,
  hideEq = false,
}) => {
  const settings = SoundFx.getDefaultEffects(effects);
  const [showEq, setShowEq] = useState(false);
  const update = (name, value) => {
    onChange({
      ...settings,
      [name]: value,
    });
  };

  const toggle = (name) => {
    update(name, !settings[name]);
  };

  return (
    <div className={`sound-fx-controls ${compact ? "compact" : ""}`}>
      {!compact && !showOnlyEq && (
        <>
          <div className="sound-fx-buttons">
            <button
              type="button"
              className={settings.denoiseEnabled ? "active" : ""}
              onClick={() => toggle("denoiseEnabled")}
            >
              🧹 Denoise
            </button>

            <button
              type="button"
              className={settings.lowCutEnabled ? "active" : ""}
              onClick={() => toggle("lowCutEnabled")}
            >
              ✂️ Low Cut
            </button>

            <button
              type="button"
              className={settings.highCutEnabled ? "active" : ""}
              onClick={() => toggle("highCutEnabled")}
            >
              🌙 High Cut
            </button>

            <button
              type="button"
              className={settings.compressionEnabled ? "active" : ""}
              onClick={() => toggle("compressionEnabled")}
            >
              🗜️ Compress
            </button>

            <button
              type="button"
              className={settings.reverbEnabled ? "active" : ""}
              onClick={() => toggle("reverbEnabled")}
            >
              🏛️ Reverb
            </button>

            <button
              type="button"
              className={settings.delayEnabled ? "active" : ""}
              onClick={() => toggle("delayEnabled")}
            >
              🔁 Delay
            </button>
          </div>

          <div className="sound-fx-sliders">
            {settings.denoiseEnabled && (
              <label>
                <span>Denoise</span>

                <input
                  type="range"
                  min="0"
                  max="100"
                  value={settings.denoiseAmount}
                  onChange={(event) =>
                    update("denoiseAmount", Number(event.target.value))
                  }
                />
              </label>
            )}

            {settings.lowCutEnabled && (
              <label>
                <span>Low Cut {settings.lowCutFrequency} Hz</span>

                <input
                  type="range"
                  min="30"
                  max="500"
                  step="5"
                  value={settings.lowCutFrequency}
                  onChange={(event) =>
                    update("lowCutFrequency", Number(event.target.value))
                  }
                />
              </label>
            )}

            {settings.highCutEnabled && (
              <label>
                <span>
                  High Cut {Math.round(settings.highCutFrequency / 1000)} kHz
                </span>

                <input
                  type="range"
                  min="2000"
                  max="20000"
                  step="250"
                  value={settings.highCutFrequency}
                  onChange={(event) =>
                    update("highCutFrequency", Number(event.target.value))
                  }
                />
              </label>
            )}

            {settings.compressionEnabled && (
              <label>
                <span>Compression</span>

                <input
                  type="range"
                  min="0"
                  max="100"
                  value={settings.compressionAmount}
                  onChange={(event) =>
                    update("compressionAmount", Number(event.target.value))
                  }
                />
              </label>
            )}

            {settings.reverbEnabled && (
              <label>
                <span>Reverb Mix</span>

                <input
                  type="range"
                  min="0"
                  max="0.8"
                  step="0.01"
                  value={settings.reverbMix}
                  onChange={(event) =>
                    update("reverbMix", Number(event.target.value))
                  }
                />
              </label>
            )}

            {settings.delayEnabled && (
              <>
                <label>
                  <span>Delay Time</span>

                  <input
                    type="range"
                    min="0.03"
                    max="1"
                    step="0.01"
                    value={settings.delayTime}
                    onChange={(event) =>
                      update("delayTime", Number(event.target.value))
                    }
                  />
                </label>

                <label>
                  <span>Delay Mix</span>

                  <input
                    type="range"
                    min="0"
                    max="0.8"
                    step="0.01"
                    value={settings.delayMix}
                    onChange={(event) =>
                      update("delayMix", Number(event.target.value))
                    }
                  />
                </label>

                <label>
                  <span>Feedback</span>

                  <input
                    type="range"
                    min="0"
                    max="0.8"
                    step="0.01"
                    value={settings.delayFeedback}
                    onChange={(event) =>
                      update("delayFeedback", Number(event.target.value))
                    }
                  />
                </label>
              </>
            )}
          </div>
        </>
      )}

      {!hideEq && (
        <div className="sound-fx-mini-eq">
          <button
            type="button"
            className={`sound-fx-eq-toggle ${showEq ? "active" : ""}`}
            onClick={() => setShowEq((previous) => !previous)}
            title="3 Band EQ"
          >
            🙂 EQ
          </button>

          {showEq && (
            <div className="sound-fx-eq-popup">
              <div className="sound-fx-eq-fader-line" />

              {[
                {
                  name: "eqLow",
                  value: settings.eqLow,
                },
                {
                  name: "eqMid",
                  value: settings.eqMid,
                },
                {
                  name: "eqHigh",
                  value: settings.eqHigh,
                },
              ].map((band) => (
                <div className="sound-fx-mini-fader" key={band.name}>
                  <span>
                    {Number(band.value) > 0 ? "+" : ""}
                    {band.value}
                  </span>

                  <input
                    type="range"
                    min="-12"
                    max="12"
                    step="1"
                    value={band.value}
                    onChange={(event) =>
                      update(band.name, Number(event.target.value))
                    }
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SoundFxControls;
